import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Search,
  CheckCircle2,
  XCircle,
  Loader2,
  Check,
  Phone,
  Wifi,
  Archive,
  MapPin,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
} from "lucide-react";
import { toast } from "sonner";

// Só a categoria "Fixa" existe hoje — o passo 1 do fluxo fica sempre
// "concluído" mostrando isso, sem precisar o vendedor escolher nada. É só
// pra dar a mesma cara de wizard de várias etapas que o sistema real da
// Vivo tem.
const UFS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
  "SP", "SE", "TO",
];
const TIPOS_COMPLEMENTO = ["Apartamento", "Bloco", "Casa", "Sala", "Fundos", "Outro"];

const CAMPOS_VAZIOS = {
  cep: "",
  numero: "",
  semNumero: false,
  logradouro: "",
  bairro: "",
  estado: "",
  cidade: "",
  tipoComplemento: "",
  complemento: "",
};

function StepIndicator({ passo }) {
  // passo 2 é o único de verdade nessa página por enquanto (o 3 —
  // "Dados do Serviço" — é só decorativo até termos os planos).
  const Step = ({ n, label, sub }) => {
    const concluido = n < passo;
    const ativo = n === passo;
    return (
      <div className="flex flex-col items-center gap-1.5 flex-1">
        <div
          className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
            concluido
              ? "bg-purple-600 border-purple-600 text-white"
              : ativo
              ? "border-purple-600 text-purple-600"
              : "border-zinc-300 text-zinc-400 dark:border-zinc-700"
          }`}
        >
          {concluido ? <Check className="h-3.5 w-3.5" /> : ativo ? <span className="h-2 w-2 rounded-full bg-purple-600 block" /> : n}
        </div>
        <span className={`text-[11px] font-bold tracking-wide ${ativo || concluido ? "text-purple-700 dark:text-purple-400" : "text-zinc-400"}`}>
          {label}
        </span>
        {sub && <span className="text-[10.5px] text-zinc-400">{sub}</span>}
      </div>
    );
  };
  return (
    <div className="flex items-start mb-6 max-w-md">
      <Step n={1} label="CATEGORIA" sub="Fixa" />
      <div className="h-0.5 flex-1 bg-purple-600 mt-3.5" />
      <Step n={2} label="COBERTURA" />
      <div className={`h-0.5 flex-1 mt-3.5 ${passo > 2 ? "bg-purple-600" : "bg-zinc-200 dark:bg-zinc-700"}`} />
      <Step n={3} label="DADOS DO SERVIÇO" />
    </div>
  );
}

// Caixa "isso é só uma simulação" — aparece tanto na etapa de confirmação
// de endereço quanto no resultado, igual ao simulador real da Vivo.
function DisclaimerBox() {
  return (
    <div className="mt-5 border border-dashed border-purple-300 dark:border-purple-800 bg-purple-50 dark:bg-purple-950/20 rounded-2xl p-4 text-center">
      <Clock className="h-5 w-5 mx-auto mb-1.5 text-purple-600" />
      <div className="text-xs font-bold text-purple-700 dark:text-purple-400 mb-1">Apenas simulação</div>
      <div className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-relaxed">
        A simulação não garante a venda. A viabilidade e disponibilidade dependem dos dados do cliente.
      </div>
    </div>
  );
}

// Nada disso vem da base de cobertura de verdade (que só sabe dizer se a
// rua tem ou não fibra) — são números plausíveis derivados do próprio
// endereço, só pra a tela ficar com a cara do sistema real da Vivo. Sempre
// dá o mesmo resultado pro mesmo endereço (não é aleatório a cada clique).
function gerarDetalhesFicticios(seedTexto) {
  let h = 0;
  for (let i = 0; i < seedTexto.length; i++) h = (h * 31 + seedTexto.charCodeAt(i)) >>> 0;
  const linhas = 18 + (h % 40);
  const caixa = 1 + (h % 24);
  const armarioNum = 10000 + (h % 9000);
  const armarioLetras = String.fromCharCode(65 + (h % 26)) + "C";
  return {
    linhasTelefonicas: linhas,
    armarioId: `${armarioNum}N${armarioLetras[0]}`,
    caixa,
  };
}

export default function VivoAgentsPage() {
  const [step, setStep] = useState("form"); // "form" | "prioridade" | "analisando" | "resultado"
  const [campos, setCampos] = useState(CAMPOS_VAZIOS);
  const [cepRegional, setCepRegional] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [resultado, setResultado] = useState(null); // { tem_cobertura }

  // Modal "Não sei meu CEP" — busca a rua na nossa base pelo nome, dentro
  // da cidade escolhida, sem precisar do CEP.
  const [modalAberto, setModalAberto] = useState(false);
  const [buscaEstado, setBuscaEstado] = useState("");
  const [buscaCidade, setBuscaCidade] = useState("");
  const [buscaTermo, setBuscaTermo] = useState("");
  const [buscandoRua, setBuscandoRua] = useState(false);
  const [ruasEncontradas, setRuasEncontradas] = useState(null); // null = ainda não buscou
  const debounceRef = useRef(null);

  const abrirModalBuscaCep = () => {
    setBuscaEstado(campos.estado || "");
    setBuscaCidade(campos.cidade || "");
    setBuscaTermo("");
    setRuasEncontradas(null);
    setModalAberto(true);
  };

  useEffect(() => {
    if (!modalAberto) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (buscaTermo.trim().length < 2 || !buscaCidade.trim()) {
      setRuasEncontradas(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBuscandoRua(true);
      try {
        const { data } = await api.post("/agentes-vivo/buscar-rua", {
          estado: buscaEstado,
          cidade: buscaCidade,
          termo: buscaTermo,
        });
        setRuasEncontradas(data.encontradas || []);
      } catch (e) {
        setRuasEncontradas([]);
      } finally {
        setBuscandoRua(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [buscaTermo, buscaCidade, buscaEstado, modalAberto]);

  const escolherRuaEncontrada = (rua) => {
    setCampos((c) => ({ ...c, logradouro: rua, cidade: buscaCidade, estado: buscaEstado || c.estado, cep: "" }));
    setCepRegional(true); // sem CEP, então a rua já entra como obrigatória
    setModalAberto(false);
    toast.success("Rua selecionada — confere bairro/número e clica em \"Próximo passo\".");
  };

  const setCampo = (campo, valor) => setCampos((c) => ({ ...c, [campo]: valor }));

  const handleCepChange = async (valor) => {
    const digits = valor.replace(/\D/g, "").slice(0, 8);
    setCampo("cep", digits);
    setResultado(null);

    if (digits.length !== 8) return;

    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await resp.json();
      if (data.erro) {
        toast.error("CEP não encontrado.");
        return;
      }
      const regional = !data.logradouro;
      setCepRegional(regional);
      setCampos((c) => ({
        ...c,
        logradouro: regional ? "" : (data.logradouro || ""),
        bairro: regional ? "" : (data.bairro || ""),
        cidade: data.localidade || c.cidade,
        estado: data.uf || c.estado,
      }));
      toast.success(
        regional
          ? "CEP regional — preenchemos cidade e estado. Informe a rua abaixo."
          : "Endereço preenchido automaticamente!"
      );
    } catch (e) {
      toast.error("Erro ao consultar o CEP — preenche manualmente.");
    } finally {
      setBuscandoCep(false);
    }
  };

  const ruaObrigatoria = cepRegional;
  const podeVerificar =
    campos.cep.length === 8 &&
    (!ruaObrigatoria || campos.logradouro.trim().length > 0) &&
    campos.bairro.trim().length > 0 &&
    campos.estado.length === 2 &&
    (campos.semNumero || campos.numero.trim().length > 0);

  const irParaPrioridade = () => {
    if (!podeVerificar) {
      toast.error("Preenche CEP, número (ou marca \"Sem número\"), endereço, bairro e estado.");
      return;
    }
    setStep("prioridade");
  };

  const efetuarAnalise = async () => {
    setStep("analisando");
    setResultado(null);
    try {
      // Espera mínima de ~900ms mesmo se a API responder na hora — só pra
      // não "piscar" a tela de "Aguarde... Realizando Análise" instantaneamente.
      const [{ data }] = await Promise.all([
        api.post("/agentes-vivo/verificar-cobertura", {
          cep: campos.cep,
          cidade: campos.cidade,
          bairro: campos.bairro,
          logradouro: campos.logradouro,
        }),
        new Promise((r) => setTimeout(r, 900)),
      ]);
      setResultado(data);
      setStep("resultado");
    } catch (e) {
      toast.error("Não foi possível consultar a cobertura agora. Tente de novo em instantes.");
      setStep("prioridade");
    }
  };

  const novaAnalise = () => {
    setCampos(CAMPOS_VAZIOS);
    setCepRegional(false);
    setResultado(null);
    setStep("form");
  };

  const detalhesFicticios = useMemo(
    () => gerarDetalhesFicticios(`${campos.cep}${campos.numero}${campos.logradouro}`),
    [campos.cep, campos.numero, campos.logradouro]
  );

  const cepFormatado = campos.cep.length === 8 ? `${campos.cep.slice(0, 5)}-${campos.cep.slice(5)}` : campos.cep;
  const enderecoLinha = `${campos.logradouro.toUpperCase()}, ${campos.semNumero ? "S/N" : campos.numero} - ${campos.bairro}, CEP: ${cepFormatado}, ${campos.cidade.toUpperCase()}/${campos.estado}`;
  const enderecoResumo = `${(campos.logradouro || campos.bairro).toUpperCase()} (GPON)`;

  return (
    <div className="p-6 md:p-8 max-w-[700px]">
      <div className="mb-6">
        <p className="text-xs font-semibold text-purple-600 tracking-wide uppercase mb-1.5">Para revendedores e agentes Vivo</p>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          Agentes de Venda Vivo
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2 max-w-2xl">
          Informe o endereço do cliente pra checar cobertura de fibra Vivo (base de ruas do Brasil todo).
        </p>
      </div>

      <StepIndicator passo={step === "form" ? 1 : step === "resultado" ? 3 : 2} />

      {step === "form" && (
      <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl shadow-sm shadow-zinc-200/60 dark:shadow-none p-7 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">CEP *</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={campos.cep}
                  onChange={(e) => handleCepChange(e.target.value)}
                  placeholder="CEP do cliente"
                  inputMode="numeric"
                  maxLength={8}
                  className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400 font-mono"
                  data-testid="vivo-cep-input"
                />
                {buscandoCep && (
                  <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-3 text-zinc-400" />
                )}
              </div>
              <Button
                type="button"
                className="rounded-full h-11 bg-purple-600 hover:bg-purple-700 text-white whitespace-nowrap text-xs px-4 font-semibold shadow-sm shadow-purple-600/20"
                onClick={abrirModalBuscaCep}
              >
                Não sei meu CEP
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">Número *</Label>
            <div className="flex items-center gap-2">
              <Input
                value={campos.numero}
                onChange={(e) => setCampo("numero", e.target.value.replace(/\D/g, ""))}
                placeholder="Número da residência"
                disabled={campos.semNumero}
                className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400"
                data-testid="vivo-numero-input"
              />
              <label className="flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400 whitespace-nowrap shrink-0">
                <Checkbox
                  checked={campos.semNumero}
                  onCheckedChange={(v) => setCampo("semNumero", !!v)}
                />
                Sem número
              </label>
            </div>
          </div>

          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">
              Endereço {ruaObrigatoria && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={campos.logradouro}
              onChange={(e) => setCampo("logradouro", e.target.value)}
              placeholder="Rua, avenida, etc"
              className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400"
              data-testid="vivo-rua-input"
            />
          </div>

          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">Bairro *</Label>
            <Input
              value={campos.bairro}
              onChange={(e) => setCampo("bairro", e.target.value)}
              placeholder="Bairro do cliente"
              className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400"
              data-testid="vivo-bairro-input"
            />
          </div>

          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">UF - Estado *</Label>
            <Select value={campos.estado} onValueChange={(v) => setCampo("estado", v)}>
              <SelectTrigger className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400" data-testid="vivo-estado-select">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {UFS.map((uf) => (
                  <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">Cidade *</Label>
            <Input
              value={campos.cidade}
              disabled
              className="rounded-xl h-11 border-zinc-400 bg-zinc-50 dark:bg-zinc-800 text-zinc-500"
              data-testid="vivo-cidade-input"
            />
          </div>

          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">Tipo de Complemento</Label>
            <Select value={campos.tipoComplemento} onValueChange={(v) => setCampo("tipoComplemento", v)}>
              <SelectTrigger className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {TIPOS_COMPLEMENTO.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[13px] font-semibold text-purple-700 dark:text-purple-400 mb-1.5 block">Complemento</Label>
            <Input
              value={campos.complemento}
              onChange={(e) => setCampo("complemento", e.target.value)}
              disabled={!campos.tipoComplemento}
              className="rounded-xl h-11 border-zinc-400 disabled:bg-zinc-50 dark:disabled:bg-zinc-800"
            />
          </div>
        </div>

        <button
          type="button"
          className="w-full mt-4 border border-dashed border-purple-300 text-purple-600 font-medium rounded-xl py-2.5 text-sm hover:bg-purple-50 dark:hover:bg-purple-950/20 transition-colors"
          onClick={() => setCampo("tipoComplemento", campos.tipoComplemento ? "" : "Apartamento")}
        >
          Novo Complemento
        </button>

        <Button
          onClick={irParaPrioridade}
          disabled={!podeVerificar}
          className="rounded-full h-12 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-md shadow-purple-600/25 w-full mt-6 text-[15px] font-semibold"
          data-testid="vivo-verificar-btn"
        >
          <Search className="h-4 w-4 mr-2" />
          Próximo passo
        </Button>
      </div>
      )}

      {(step === "prioridade" || step === "analisando") && (
        <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-2xl shadow-sm shadow-zinc-200/60 dark:shadow-none p-7 mb-6">
          <div className="flex items-center justify-between mb-5">
            <button type="button" disabled className="h-8 w-8 rounded-full border border-zinc-300 dark:border-zinc-700 flex items-center justify-center opacity-40 shrink-0">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="font-display font-bold text-[15px]">Prioridade</span>
            <button type="button" disabled className="h-8 w-8 rounded-full border border-zinc-300 dark:border-zinc-700 flex items-center justify-center opacity-40 shrink-0">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {step === "prioridade" ? (
            <>
              <p className="text-center text-xs font-bold text-purple-700 dark:text-purple-400 mb-4">
                Selecione o endereço correspondente
              </p>

              <label className="flex items-start gap-3 border border-purple-200 dark:border-purple-900 rounded-xl px-4 py-3 mb-5 cursor-default">
                <input type="radio" checked readOnly className="mt-1 accent-purple-600" data-testid="vivo-endereco-radio" />
                <div>
                  <div className="text-sm font-bold text-purple-700 dark:text-purple-400">{enderecoResumo}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    CEP:{campos.cep} Nº:{campos.semNumero ? "S/N" : campos.numero} Rede:VIVO!
                  </div>
                </div>
              </label>

              <Button
                onClick={efetuarAnalise}
                className="rounded-full h-12 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-md shadow-purple-600/25 w-full font-semibold"
                data-testid="vivo-efetuar-analise-btn"
              >
                Efetuar análise
              </Button>
              <button
                type="button"
                onClick={() => setStep("form")}
                className="w-full mt-3 h-11 rounded-full border border-zinc-300 dark:border-zinc-700 text-sm font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
              >
                Voltar
              </button>
            </>
          ) : (
            <div className="text-center py-6">
              <Loader2 className="h-9 w-9 mx-auto mb-4 animate-spin text-purple-600" />
              <div className="font-display font-bold text-purple-700 dark:text-purple-400 mb-2">Aguarde... Realizando Análise</div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed max-w-xs mx-auto">
                Isso pode demorar até 2 minutos. Se algo der errado, avisaremos.
              </p>
            </div>
          )}

          <DisclaimerBox />
        </div>
      )}

      {step === "resultado" && resultado && (
        <div
          className={`border rounded-2xl p-6 mb-6 shadow-sm ${
            resultado.tem_cobertura
              ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-900"
              : "bg-zinc-50 border-zinc-200 dark:bg-zinc-900 dark:border-zinc-700"
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            {resultado.tem_cobertura ? (
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-zinc-400" />
            )}
            <span className="font-display font-semibold text-green-700 dark:text-green-400">
              {resultado.tem_cobertura ? "ENDEREÇO COM COBERTURA" : "SEM COBERTURA NESSE ENDEREÇO"}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-sm text-zinc-600 dark:text-zinc-300 mb-4 ml-7">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            {enderecoLinha}
          </div>

          {resultado.tem_cobertura && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ml-7">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                  <Phone className="h-4 w-4 text-green-600" /> Linhas Telefônicas: {detalhesFicticios.linhasTelefonicas}
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold mb-1">
                  <Wifi className="h-4 w-4 text-green-600" /> Bandas Largas Disponíveis:
                </div>
                <ul className="text-sm text-zinc-600 dark:text-zinc-300 ml-6 space-y-0.5">
                  <li>{detalhesFicticios.linhasTelefonicas} Até 20Mbps</li>
                  <li>{detalhesFicticios.linhasTelefonicas} De 21 a 50Mbps</li>
                  <li>{detalhesFicticios.linhasTelefonicas} De 51 a 1 Gbps</li>
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold mb-2">
                  <Archive className="h-4 w-4 text-green-600" /> Armário:
                </div>
                <div className="text-sm text-zinc-600 dark:text-zinc-300 space-y-0.5">
                  <div>ID: {detalhesFicticios.armarioId}</div>
                  <div>Status: <span className="text-green-600 font-semibold">Disponível</span></div>
                  <div>Tipo: PADRÃO</div>
                  <div>Caixa: {detalhesFicticios.caixa}</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3 mt-5">
            {resultado.tem_cobertura && (
              <Button
                className="rounded-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-md shadow-purple-600/25 font-semibold w-full"
                onClick={() => toast.info("Próxima etapa (Dados do Serviço) chega em breve.")}
              >
                Próximo passo
              </Button>
            )}
            <button
              type="button"
              className="text-sm font-semibold text-purple-700 border border-purple-500 rounded-full px-5 py-2.5 hover:bg-purple-50 dark:hover:bg-purple-950/20 transition-colors w-full"
              onClick={novaAnalise}
            >
              Nova Análise
            </button>
            <button
              type="button"
              className="text-sm font-semibold border border-zinc-300 dark:border-zinc-700 rounded-full px-5 py-2.5 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors w-full"
              onClick={() => setStep("prioridade")}
            >
              Voltar
            </button>
          </div>

          <DisclaimerBox />
        </div>
      )}

      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Busca de CEP</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-purple-700 dark:text-purple-400 font-semibold mb-1 block">
                Endereço <span className="text-zinc-400 font-normal">- (logradouro, av...)</span>
              </Label>
              <Input
                autoFocus
                value={buscaTermo}
                onChange={(e) => setBuscaTermo(e.target.value)}
                placeholder="Nome da rua"
                className="rounded-xl h-11 border-purple-300 focus-visible:ring-purple-500/40 focus-visible:border-purple-400"
              />
            </div>

            <div>
              <Label className="text-purple-700 dark:text-purple-400 font-semibold mb-1 block">Estado (UF)</Label>
              <Select value={buscaEstado} onValueChange={setBuscaEstado}>
                <SelectTrigger className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {UFS.map((uf) => (
                    <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-purple-700 dark:text-purple-400 font-semibold mb-1 block">Cidade</Label>
              <Input
                value={buscaCidade}
                onChange={(e) => setBuscaCidade(e.target.value)}
                placeholder="Cidade do cliente"
                className="rounded-xl h-11 border-zinc-400 focus-visible:ring-purple-500/40 focus-visible:border-purple-400"
              />
            </div>

            <div className="bg-zinc-50 dark:bg-zinc-800/60 rounded-xl border border-zinc-200 dark:border-zinc-700 min-h-[52px] flex flex-col justify-center overflow-hidden">
              {buscandoRua ? (
                <div className="flex items-center justify-center gap-2 text-sm text-zinc-500 py-3">
                  <Loader2 className="h-4 w-4 animate-spin" /> Procurando...
                </div>
              ) : ruasEncontradas === null ? (
                <div className="text-center text-sm text-zinc-400 py-3">
                  Digite o nome da rua e a cidade pra buscar.
                </div>
              ) : ruasEncontradas.length === 0 ? (
                <div className="flex items-center justify-center gap-2 text-sm text-red-600 py-3">
                  <AlertCircle className="h-4 w-4" /> Rua sem viabilidade.
                </div>
              ) : (
                <div className="divide-y divide-zinc-200 dark:divide-zinc-700">
                  {ruasEncontradas.map((rua) => (
                    <button
                      key={rua}
                      type="button"
                      onClick={() => escolherRuaEncontrada(rua)}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-purple-50 dark:hover:bg-purple-950/20 flex items-center gap-2"
                    >
                      <MapPin className="h-3.5 w-3.5 text-purple-500 shrink-0" />
                      {rua}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
